import * as apiAdministration from "@api/administration";
import AnnuncioPreview from '@templates/annunci/AnnuncioPreview';
import Button from '@components/button';
import Card from '@components/card';
import Icon from "@components/icon";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import SelectCategory from "@templates/selectCategory";
import SelectProvince from "@templates/selectProvince";
import TextInput from "@components/textInput";
import moment from "moment";
import styles from "./AdminValidazione.module.css";
import { useCategories } from "@contexts/CategoriesContext";
import { useDispatch } from "react-redux";
import { useSnackbars } from "@contexts/SnackbarsContext";
import { ROUTES, useNavigator } from "@contexts/NavigatorContext";
import { editAdvertisement, getAdvertisementImageUrl } from "@logic/annuncio";

const AdminValidazione = () => {
  const dispatch = useDispatch();
  const { getCategoryDescriptionById } = useCategories();
  const { newWindow } = useNavigator();
  const { openSnackbar } = useSnackbars();

  const [loading, setLoading] = useState(false);
  const [advertisments, setAdvertisments] = useState([]);
  const [selectedAdvertisementId, setSelectedAdvertisementId] = useState(null);
  const [selectedAdvertisementIds, setSelectedAdvertisementIds] = useState([]);
  const [formCategory, setFormCategory] = useState(null);
  const [formProvince, setFormProvince] = useState("");
  const [formTitle, setFormTitle] = useState("");
  const [formCity, setFormCity] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formError, setFormError] = useState(null);

  const hasSelectedAdvertisements = selectedAdvertisementIds.length > 0;
  const areAllAdvertisementsSelected =
    advertisments.length > 0 && selectedAdvertisementIds.length === advertisments.length;

  const loadAnnunciDaValidare = useCallback(() => {
    setLoading(true);
    return apiAdministration.GetAdvertismentsWaitingForValidation()
      .then((data) => {
        setAdvertisments(data);
        setSelectedAdvertisementIds([]);
      })
      .catch((e) => openSnackbar("❌ " + e.message))
      .finally(() => setLoading(false));
  }, [openSnackbar]);

  const validateAdvertisement = useCallback((annuncio) => {
    setLoading(true);
    apiAdministration.ValidateAdvertisement({ advertisementId: annuncio.id })
      .then(() => loadAnnunciDaValidare())
      .catch((e) => {
        openSnackbar("❌ " + e.message);
        setLoading(false);
      });
  }, [loadAnnunciDaValidare, openSnackbar]);

  const refuseAdvertisement = useCallback((annuncio) => {
    setLoading(true);
    apiAdministration.RefuseAdvertisement({ advertisementId: annuncio.id })
      .then(() => loadAnnunciDaValidare())
      .catch((e) => {
        openSnackbar("❌ " + e.message);
        setLoading(false);
      });
  }, [loadAnnunciDaValidare, openSnackbar]);

  const deleteAdvertisement = useCallback((annuncio) => {
    setLoading(true);
    apiAdministration.DeleteAdvertisement({ advertisementId: annuncio.id })
      .then(() => loadAnnunciDaValidare())
      .catch((e) => {
        openSnackbar("❌ " + e.message);
        setLoading(false);
      });
  }, [loadAnnunciDaValidare, openSnackbar]);

  const selectedAdvertisement = useMemo(() => (
    advertisments.find((x) => x.id === selectedAdvertisementId) || null
  ), [advertisments, selectedAdvertisementId]);

  const mainImageUrl = useMemo(() => {
    if (!selectedAdvertisement?.images || selectedAdvertisement.images.length === 0) {
      return null;
    }
    return getAdvertisementImageUrl({
      userId: selectedAdvertisement.userId,
      advertisementId: selectedAdvertisement.id,
      imageId: selectedAdvertisement.images[0].id,
    });
  }, [selectedAdvertisement]);

  const formattedPublishDate = useMemo(() => {
    if (!selectedAdvertisement?.publishDate || selectedAdvertisement.publishDate === "0001-01-01T00:00:00Z") {
      return "Non ancora pubblicato";
    }
    return moment(selectedAdvertisement.publishDate).format("D MMMM YYYY");
  }, [selectedAdvertisement]);

  const formattedExpirationDate = useMemo(() => {
    if (!selectedAdvertisement?.expirationDate) {
      return null;
    }
    return moment(selectedAdvertisement.expirationDate).format("D MMMM YYYY");
  }, [selectedAdvertisement]);

  const selectAdvertisement = useCallback((advertisementId) => {
    if (loading) {
      return;
    }
    setSelectedAdvertisementId(advertisementId);
  }, [loading]);

  const toggleAdvertisementSelection = useCallback((advertisementId) => {
    setSelectedAdvertisementIds((prevSelected) => {
      if (prevSelected.includes(advertisementId)) {
        return prevSelected.filter((id) => id !== advertisementId);
      }
      return [...prevSelected, advertisementId];
    });
  }, []);

  const selectAllAdvertisements = useCallback(() => {
    setSelectedAdvertisementIds(advertisments.map((annuncio) => annuncio.id));
  }, [advertisments]);

  const performBulkAction = useCallback(async (apiMethod, successMessage) => {
    if (!selectedAdvertisementIds.length) {
      return;
    }

    try {
      setLoading(true);
      const results = await Promise.allSettled(
        selectedAdvertisementIds.map((advertisementId) =>
          apiMethod({ advertisementId })
        )
      );

      const rejected = results.filter((result) => result.status === "rejected");
      if (rejected.length === 0 && successMessage) {
        openSnackbar(successMessage);
      }
      if (rejected.length > 0) {
        openSnackbar("❌ Alcune operazioni non sono andate a buon fine");
      }
    } catch (e) {
      openSnackbar("❌ " + e.message);
    } finally {
      await loadAnnunciDaValidare();
    }
  }, [loadAnnunciDaValidare, openSnackbar, selectedAdvertisementIds]);

  const onValidateSelected = useCallback(() => {
    performBulkAction(apiAdministration.ValidateAdvertisement, "✨ Annunci validati!");
  }, [performBulkAction]);

  const onDeleteSelected = useCallback(() => {
    if (!selectedAdvertisementIds.length) {
      return;
    }

    const confirmed = window.confirm("Eliminare tutti gli annunci selezionati?");
    if (!confirmed) {
      return;
    }

    performBulkAction(apiAdministration.DeleteAdvertisement, "🗑️ Annunci eliminati");
  }, [performBulkAction, selectedAdvertisementIds.length]);

  const onValidate = useCallback(() => {
    if (!selectedAdvertisement) {
      return;
    }
    validateAdvertisement(selectedAdvertisement);
  }, [selectedAdvertisement, validateAdvertisement]);

  const onRefuse = useCallback(() => {
    if (!selectedAdvertisement) {
      return;
    }
    refuseAdvertisement(selectedAdvertisement);
  }, [selectedAdvertisement, refuseAdvertisement]);

  const onDelete = useCallback(() => {
    if (!selectedAdvertisement) {
      return;
    }

    const confirmed = window.confirm(`Eliminare l'annuncio "${selectedAdvertisement.title}"?`);
    if (!confirmed) {
      return;
    }

    deleteAdvertisement(selectedAdvertisement);
  }, [deleteAdvertisement, selectedAdvertisement]);

  const onOpenAnnouncement = useCallback(() => {
    if (!selectedAdvertisement) {
      return;
    }
    newWindow(ROUTES.ANNUNCIO, [selectedAdvertisement.id]);
  }, [newWindow, selectedAdvertisement]);

  const onOpenUser = useCallback(() => {
    if (!selectedAdvertisement?.userId) {
      return;
    }
    newWindow(ROUTES.UTENTE, [selectedAdvertisement.userId]);
  }, [newWindow, selectedAdvertisement]);

  const onSaveChanges = useCallback(() => {
    if (!selectedAdvertisement) {
      return;
    }

    setLoading(true);
    setFormError(null);
    dispatch(editAdvertisement({
      advertisementId: selectedAdvertisement.id,
      title: formTitle,
      description: formDescription,
      categoryId: formCategory,
      province: formProvince,
      city: formCity,
      newImagesBlobs: [],
      deletedImageIds: [],
    }))
      .then(() => {
        openSnackbar("✨ Modifiche salvate!");
        loadAnnunciDaValidare();
      })
      .catch((e) => {
        openSnackbar("❌ " + e.message);
        setFormError(e.message);
        setLoading(false);
      });
  }, [dispatch, selectedAdvertisement, formTitle, formDescription, formCategory, formProvince, formCity, openSnackbar, loadAnnunciDaValidare]);

  useEffect(() => {
    loadAnnunciDaValidare();
  }, [loadAnnunciDaValidare]);

  useEffect(() => {
    setSelectedAdvertisementIds((prevSelected) =>
      prevSelected.filter((id) => advertisments.some((annuncio) => annuncio.id === id))
    );
  }, [advertisments]);

  useEffect(() => {
    if (!advertisments.length) {
      setSelectedAdvertisementId(null);
      return;
    }

    if (!selectedAdvertisementId) {
      setSelectedAdvertisementId(advertisments[0].id);
      return;
    }

    const stillExists = advertisments.some((x) => x.id === selectedAdvertisementId);
    if (!stillExists) {
      setSelectedAdvertisementId(advertisments[0].id);
    }
  }, [advertisments, selectedAdvertisementId]);

  useEffect(() => {
    if (!selectedAdvertisement) {
      setFormCategory(null);
      setFormProvince("");
      setFormTitle("");
      setFormCity("");
      setFormDescription("");
      return;
    }

    setFormCategory(selectedAdvertisement.categoryId || null);
    setFormProvince(selectedAdvertisement.province || "");
    setFormTitle(selectedAdvertisement.title || "");
    setFormCity(selectedAdvertisement.city || "");
    setFormDescription(selectedAdvertisement.description || "");
  }, [selectedAdvertisement]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageTitleWrapper}>
        <span className='page-title'>Validazione</span>
      </div>
      <div className={styles.pageWrapper}>
        <section className={styles.listSection}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionHeaderInfo}>
              <span className={styles.sectionTitle}>Annunci in attesa</span>
              {advertisments.length > 0 && (
                <span className={styles.sectionCount}>{advertisments.length}</span>
              )}
            </div>
            {advertisments.length > 0 && (
              <div className={styles.sectionActions}>
                <Button
                  color="primary"
                  disabled={loading || !hasSelectedAdvertisements}
                  icon="task_alt"
                  onClick={onValidateSelected}
                  size="mini"
                  text="Valida tutti"
                />
                <Button
                  color="secondary"
                  disabled={loading || !hasSelectedAdvertisements}
                  icon="delete"
                  onClick={onDeleteSelected}
                  size="mini"
                  text="Elimina tutti"
                />
                <Button
                  color="secondary"
                  disabled={loading || areAllAdvertisementsSelected}
                  icon="select_check_box"
                  onClick={selectAllAdvertisements}
                  size="mini"
                  text="Seleziona tutti"
                />
              </div>
            )}
          </div>
          <div className={styles.listContainer}>
            {loading && advertisments.length === 0 && (
              <div className={styles.emptyState}>Caricamento annunci…</div>
            )}
            {!loading && advertisments.length === 0 && (
              <div className={styles.emptyState}>Nessun annuncio in attesa di validazione.</div>
            )}
            {advertisments.map((annuncio) => {
              const itemClassName = [
                styles.listItem,
                selectedAdvertisementId === annuncio.id ? styles.listItemSelected : null,
              ].filter(Boolean).join(" ");

              const isSelectedForBulk = selectedAdvertisementIds.includes(annuncio.id);

              const onKeyDown = (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  selectAdvertisement(annuncio.id);
                }
              };

              return (
                <div
                  key={annuncio.id}
                  className={itemClassName}
                  onClick={() => selectAdvertisement(annuncio.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={onKeyDown}
                >
                  <div className={styles.listItemContent}>
                    <input
                      type="checkbox"
                      className={styles.listItemCheckbox}
                      checked={isSelectedForBulk}
                      onChange={(event) => {
                        event.stopPropagation();
                        toggleAdvertisementSelection(annuncio.id);
                      }}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                      disabled={loading}
                    />
                    <AnnuncioPreview
                      annuncio={annuncio}
                      suppressNavigation
                      loading={loading}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        <section className={styles.detailsSection}>
          {!selectedAdvertisement && (
            <div className={styles.emptyState}>Seleziona un annuncio per visualizzarne i dettagli.</div>
          )}
          {selectedAdvertisement && (
            <>
              <Card>
                <div className={styles.cardContent}>
                  <span className='page-section'>Anteprima risultati di ricerca</span>
                  <AnnuncioPreview
                    annuncio={selectedAdvertisement}
                    suppressNavigation
                  />
                </div>
              </Card>
              <Card>
                <div className={styles.cardContent}>
                  <span className='page-section'>Anteprima annuncio</span>
                  {mainImageUrl ? (
                    <img
                      src={mainImageUrl}
                      alt={`Immagine principale annuncio ${selectedAdvertisement.title}`}
                      className={styles.fullPreviewImage}
                    />
                  ) : (
                    <div className={styles.fullPreviewPlaceholder}>
                      <Icon name="image" size={48} fill={0} weight={400} grade={0} opticalSize={48} />
                      <span>Nessuna immagine disponibile</span>
                    </div>
                  )}
                  <div className={styles.fullPreviewInfo}>
                    <span className={styles.fullPreviewTitle}>{selectedAdvertisement.title}</span>
                    <span className={styles.fullPreviewLocation}>{`${selectedAdvertisement.city} (${selectedAdvertisement.province})`}</span>
                    <p className={`${styles.fullPreviewDescription} selectable`}>{selectedAdvertisement.description}</p>
                    <div className={styles.fullPreviewMeta}>
                      <span>Categoria: {getCategoryDescriptionById(selectedAdvertisement.categoryId) || "-"}</span>
                      <span>Pubblicazione: {formattedPublishDate}</span>
                      {formattedExpirationDate && (
                        <span>Scadenza: {formattedExpirationDate}</span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </>
          )}
        </section>
        <section>
          {selectedAdvertisement && (
            <>
          <div className={styles.detailHeader}>
            <div className={styles.detailActions}>
              <Button
                color="primary"
                disabled={loading}
                icon="thumb_up"
                onClick={onValidate}
                size="mini"
                text="Valida"
              />
              <Button
                color="secondary"
                disabled={loading}
                icon="thumb_down"
                onClick={onRefuse}
                size="mini"
                text="Rifiuta"
              />
              <Button
                color="secondary"
                disabled={loading}
                icon="delete"
                onClick={onDelete}
                size="mini"
                text="Elimina"
              />
              <Button
                color="secondary"
                disabled={loading}
                icon="open_in_new"
                onClick={onOpenAnnouncement}
                size="mini"
                text="Apri annuncio"
              />
              <Button
                color="secondary"
                disabled={loading}
                icon="open_in_new"
                onClick={onOpenUser}
                size="mini"
                text={`Utente ${selectedAdvertisement.user?.advertisementName || ""}`}
              />
            </div>
          </div>
          <Card>
            <div className={styles.cardContent}>
              <span className='page-section'>Modifica annuncio</span>
              <div className={styles.editForm}>
                <SelectCategory
                  value={formCategory}
                  setValue={setFormCategory}
                  disabled={loading}
                />
                <TextInput
                  label="Titolo"
                  value={formTitle}
                  setValue={setFormTitle}
                  disabled={loading}
                />
                <SelectProvince
                  label="Provincia"
                  value={formProvince}
                  setValue={setFormProvince}
                  disabled={loading}
                />
                <TextInput
                  label="Località"
                  value={formCity}
                  setValue={setFormCity}
                  disabled={loading}
                />
                <TextInput
                  label="Descrizione"
                  value={formDescription}
                  setValue={setFormDescription}
                  disabled={loading}
                />
                {formError && (
                  <div className={styles.formError}>{formError}</div>
                )}
                <div className={styles.formActions}>
                  <Button
                    color="primary"
                    disabled={loading}
                    icon="save"
                    onClick={onSaveChanges}
                    text="Salva modifiche"
                  />
                </div>
              </div>
            </div>
          </Card>
          </>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminValidazione;
